import React, { useEffect, useMemo, useRef, useState } from "react";
import { isValidCustomerEmail } from "../../validation/customerEmail";
import { formatRubPrice, formatUsdtPrice, getMaxPurchasableQuantity } from "../helpers";
import {
  checkoutOtherStore,
  fetchOtherStoreUiConfig,
} from "../api";
import { saveRecentStoreOrder } from "../order-storage";
import { DEFAULT_SUPPORT_CONTACTS } from "../order-status";
import { getReferralPayloadForCheckout } from "../referral-storage";

const CATALOG_LOADING_COUNTDOWN_START = 20;

const PAYMENT_LOADER_HTML = `<!doctype html>
<html lang="ru">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Переходим к оплате…</title>
<style>
  html, body { height: 100%; margin: 0; }
  body {
    display: flex; align-items: center; justify-content: center;
    background: #111827; color: #e5e7eb;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    text-align: center; padding: 24px; box-sizing: border-box;
  }
  .spinner {
    width: 48px; height: 48px; margin: 0 auto 24px;
    border: 4px solid rgba(229, 231, 235, 0.2); border-top-color: #3b82f6;
    border-radius: 50%; animation: spin 0.9s linear infinite;
  }
  h1 { font-size: 20px; font-weight: 600; margin: 0 0 8px; }
  p { font-size: 15px; color: #9ca3af; margin: 0; line-height: 1.5; }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
</head>
<body>
  <div>
    <div class="spinner"></div>
    <h1>Переходим к оплате…</h1>
    <p>Сейчас откроется страница оплаты</p>
  </div>
</body>
</html>`;

function showPaymentLoader(payWindow) {
  try {
    payWindow.document.open();
    payWindow.document.write(PAYMENT_LOADER_HTML);
    payWindow.document.close();
  } catch {
    // Без экрана загрузки вкладка просто останется пустой до перехода на оплату
  }
}

function OtherStorePurchasePanel({
  store,
  catalogLoading = false,
  catalogError = "",
  onRefreshCatalog,
}) {
  const initialRegion = store.regions[0];
  const initialDenomination = initialRegion?.denominations[0];

  const [regionId, setRegionId] = useState(initialRegion?.id ?? "");
  const [denominationId, setDenominationId] = useState(
    initialDenomination?.id ?? "",
  );
  const [quantity, setQuantity] = useState(
    getMaxPurchasableQuantity(initialDenomination?.stockAvailable ?? 0) > 0
      ? 1
      : 0,
  );
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [privacyUrl, setPrivacyUrl] = useState(
    "https://trendy-eng-shop.ru/offer#privacy",
  );
  const [paymentLifetimeMinutes, setPaymentLifetimeMinutes] = useState(5);
  const [isCryptoPayment, setIsCryptoPayment] = useState(false);
  const [testingPeriodEnabled, setTestingPeriodEnabled] = useState(false);
  const [supportContacts, setSupportContacts] = useState(null);
  const [catalogCountdown, setCatalogCountdown] = useState(
    CATALOG_LOADING_COUNTDOWN_START,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [priceNotice, setPriceNotice] = useState("");
  const [emailHintVisible, setEmailHintVisible] = useState(false);
  const prevUnitPricesRef = useRef(null);
  const skipPriceNoticeRef = useRef(false);

  useEffect(() => {
    prevUnitPricesRef.current = null;
    skipPriceNoticeRef.current = false;
  }, [store.id]);

  useEffect(() => {
    if (!store.regions.length) {
      return;
    }

    const region = store.regions[0];
    const seen = new Set();
    const denominations = (region.denominations || []).filter((item) => {
      if (!item?.id || seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
    const denomination = denominations[0];
    const nextMax = getMaxPurchasableQuantity(
      denomination?.stockAvailable ?? 0,
    );

    setRegionId(region.id);
    setDenominationId(denomination?.id ?? "");
    setQuantity(nextMax > 0 ? 1 : 0);
  }, [store.id, store.regions]);

  useEffect(() => {
    if (consent) {
      setEmailHintVisible(false);
    }
  }, [consent]);

  useEffect(() => {
    fetchOtherStoreUiConfig()
      .then((data) => {
        if (data.privacyPolicyUrl) {
          setPrivacyUrl(data.privacyPolicyUrl);
        }
        if (data.paymentLifetimeMinutes) {
          setPaymentLifetimeMinutes(data.paymentLifetimeMinutes);
        }
        if (data.testingPeriodEnabled != null) {
          setTestingPeriodEnabled(Boolean(data.testingPeriodEnabled));
        }
        if (data.support) {
          setSupportContacts(data.support);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!catalogLoading) {
      setCatalogCountdown(CATALOG_LOADING_COUNTDOWN_START);
      return undefined;
    }

    setCatalogCountdown(CATALOG_LOADING_COUNTDOWN_START);
    const intervalId = window.setInterval(() => {
      setCatalogCountdown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [catalogLoading]);

  const selectedRegion = useMemo(
    () => store.regions.find((region) => region.id === regionId),
    [store.regions, regionId],
  );

  const regionDenominations = useMemo(() => {
    const list = selectedRegion?.denominations || [];
    const unique = new Map();
    for (const item of list) {
      if (item?.id && !unique.has(item.id)) {
        unique.set(item.id, item);
      }
    }
    return [...unique.values()];
  }, [selectedRegion]);

  const selectedDenomination = useMemo(
    () =>
      regionDenominations.find((item) => item.id === denominationId) ?? null,
    [regionDenominations, denominationId],
  );

  const maxQuantity = getMaxPurchasableQuantity(
    selectedDenomination?.stockAvailable ?? 0,
  );

  const handleRegionChange = (event) => {
    const newRegionId = event.target.value;
    const region = store.regions.find((item) => item.id === newRegionId);
    const seen = new Set();
    const denominations = (region?.denominations || []).filter((item) => {
      if (!item?.id || seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
    const firstDenomination = denominations[0] ?? null;
    const nextMax = getMaxPurchasableQuantity(
      firstDenomination?.stockAvailable ?? 0,
    );

    setRegionId(newRegionId);
    setDenominationId(firstDenomination?.id ?? "");
    setQuantity(nextMax > 0 ? 1 : 0);
    setError("");
  };

  const handleDenominationChange = (event) => {
    const newDenominationId = event.target.value;
    const denomination = regionDenominations.find(
      (item) => item.id === newDenominationId,
    );
    const nextMax = getMaxPurchasableQuantity(
      denomination?.stockAvailable ?? 0,
    );

    setDenominationId(newDenominationId);
    setQuantity(nextMax > 0 ? 1 : 0);
    setError("");
  };

  const totalPriceRub = selectedDenomination
    ? selectedDenomination.priceRub * quantity
    : 0;

  const totalPriceUsdt = selectedDenomination
    ? Math.round((selectedDenomination.priceCryptoUsdt || 0) * quantity * 100) /
      100
    : 0;

  const totalPrice = isCryptoPayment ? totalPriceUsdt : totalPriceRub;

  useEffect(() => {
    if (catalogLoading || !selectedDenomination || !denominationId) {
      return undefined;
    }

    const nextUnitPrices = {
      denominationId,
      priceRub: selectedDenomination.priceRub,
      priceCryptoUsdt: selectedDenomination.priceCryptoUsdt ?? 0,
    };

    if (skipPriceNoticeRef.current) {
      skipPriceNoticeRef.current = false;
      prevUnitPricesRef.current = nextUnitPrices;
      return undefined;
    }

    const prevUnitPrices = prevUnitPricesRef.current;
    const catalogPriceChanged =
      prevUnitPrices &&
      prevUnitPrices.denominationId === denominationId &&
      (prevUnitPrices.priceRub !== nextUnitPrices.priceRub ||
        prevUnitPrices.priceCryptoUsdt !== nextUnitPrices.priceCryptoUsdt);

    prevUnitPricesRef.current = nextUnitPrices;

    if (!catalogPriceChanged) {
      return undefined;
    }

    setPriceNotice("Цена обновлена");
    const timerId = window.setTimeout(() => setPriceNotice(""), 4000);
    return () => window.clearTimeout(timerId);
  }, [
    store,
    catalogLoading,
    denominationId,
    selectedDenomination,
  ]);

  const hasEmail = isValidCustomerEmail(email);

  const canPurchase =
    Boolean(selectedDenomination) &&
    quantity > 0 &&
    consent &&
    hasEmail &&
    !isSubmitting &&
    (isCryptoPayment ? totalPriceUsdt > 0 : totalPriceRub > 0);

  const getPurchaseButtonLabel = () => {
    if (isSubmitting) {
      return "Создаём платёж…";
    }
    if (totalPrice <= 0) {
      return "Недоступно";
    }
    const priceLabel = isCryptoPayment
      ? formatUsdtPrice(totalPriceUsdt)
      : formatRubPrice(totalPriceRub);
    return hasEmail ? `Оплатить ${priceLabel}` : priceLabel;
  };

  const handleDecrease = () => {
    setQuantity((current) => Math.max(1, current - 1));
  };

  const handleIncrease = () => {
    setQuantity((current) => Math.min(maxQuantity, current + 1));
  };

  const goToOrderPage = (orderId, accessToken) => {
    const tokenQuery = accessToken
      ? `?token=${encodeURIComponent(accessToken)}`
      : "";
    window.location.href = `/store/order/${orderId}${tokenQuery}`;
  };

  const handlePurchase = async () => {
    if (!canPurchase) {
      return;
    }

    setIsSubmitting(true);
    setError("");
    setPriceNotice("");

    // После await браузер часто режет window.open (нет user gesture) —
    // вкладка оплаты не открывается, а нас уже кидает на «ожидание кода».
    // Открываем blank синхронно по клику, URL подставим после ответа API.
    const payWindow = window.open("about:blank", "_blank");
    if (payWindow) {
      // Замена noopener: страница оплаты не должна управлять нашей вкладкой
      payWindow.opener = null;
      showPaymentLoader(payWindow);
    }

    try {
      const referral = getReferralPayloadForCheckout();
      const result = await checkoutOtherStore(store.id, {
        productId: denominationId,
        quantity,
        email: email.trim(),
        consent: true,
        paymentMethod: isCryptoPayment ? "crypto" : "sbp",
        expectedAmountRub: isCryptoPayment ? undefined : totalPriceRub,
        expectedAmountUsdt: isCryptoPayment ? totalPriceUsdt : undefined,
        ...referral,
      });

      if (result.orderId) {
        saveRecentStoreOrder(store.id, result.orderId, result.accessToken);
      }

      if (result.paymentUrl) {
        if (payWindow && !payWindow.closed) {
          payWindow.location.href = result.paymentUrl;
        } else {
          // Popup всё равно заблокирован — уходим на оплату в этой вкладке
          // (returnUrl/failUrl вернут на витрину / заказ).
          window.location.href = result.paymentUrl;
          return;
        }
        if (result.orderId) {
          goToOrderPage(result.orderId, result.accessToken);
        }
        return;
      }

      if (payWindow && !payWindow.closed) {
        payWindow.close();
      }

      if (result.orderId) {
        // Заказ есть, ссылки на оплату нет — на ожидание, но с ошибкой в UI
        // не уходим молча: покажем на странице заказа через обычный PENDING.
        goToOrderPage(result.orderId, result.accessToken);
        return;
      }

      setError("Не удалось создать платёж");
    } catch (purchaseError) {
      if (payWindow && !payWindow.closed) {
        payWindow.close();
      }
      if (purchaseError?.code === "PRICE_CHANGED") {
        skipPriceNoticeRef.current = true;
        await onRefreshCatalog?.({ silent: true });
        const nextAmount =
          isCryptoPayment && purchaseError.amountUsdt != null
            ? formatUsdtPrice(purchaseError.amountUsdt)
            : formatRubPrice(purchaseError.amountRub);
        setError(`${purchaseError.message} Новая сумма: ${nextAmount}.`);
        return;
      }
      setError(
        purchaseError?.message || "Не удалось оформить заказ. Попробуйте позже.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const support = {
    ...DEFAULT_SUPPORT_CONTACTS,
    ...(supportContacts || {}),
  };

  return (
    <aside className="other-store-purchase-card">
      <h2 className="other-store-purchase-title">
        Пополнение {store.label}
      </h2>

      {catalogLoading ? (
        <p className="other-store-catalog-loading">
          <span>Обновление списка доступных карт — чуть-чуть времени</span>
          <span className="other-store-catalog-countdown" aria-hidden="true">
            {catalogCountdown}
          </span>
        </p>
      ) : null}

      {!catalogLoading && catalogError ? (
        <p className="other-store-error">{catalogError}</p>
      ) : null}

      {!catalogLoading && !catalogError && store.regions.length === 0 ? (
        <p className="other-store-description">
          Номиналы временно недоступны.
        </p>
      ) : null}

      {!catalogLoading && !catalogError && store.regions.length > 0 ? (
        <div className="other-store-purchase-form">
      <label className="other-store-field">
        <span className="other-store-field-label">Регион</span>
        <select
          className="other-store-select"
          value={regionId}
          onChange={handleRegionChange}
        >
          {store.regions.map((region) => (
            <option key={region.id} value={region.id}>
              {region.label}
            </option>
          ))}
        </select>
      </label>

      <label className="other-store-field">
        <span className="other-store-field-label">Номинал</span>
        <select
          className="other-store-select"
          value={denominationId}
          onChange={handleDenominationChange}
        >
          {regionDenominations.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label} — {formatRubPrice(item.priceRub)}
            </option>
          ))}
        </select>
      </label>

      <div className="other-store-field">
        <span className="other-store-field-label">Количество</span>
        <div className="other-store-quantity-row">
          <div className="other-store-counter">
            <button
              type="button"
              className="other-store-counter-btn"
              onClick={handleDecrease}
              disabled={quantity <= 1}
              aria-label="Уменьшить количество"
            >
              −
            </button>
            <span className="other-store-counter-value">{quantity}</span>
            <button
              type="button"
              className="other-store-counter-btn"
              onClick={handleIncrease}
              disabled={quantity >= maxQuantity}
              aria-label="Увеличить количество"
            >
              +
            </button>
          </div>
          {selectedDenomination && (
            <span className="other-store-stock-hint">
              {maxQuantity > 0
                ? `Доступно: ${maxQuantity} шт.`
                : "Нет в наличии"}
            </span>
          )}
        </div>
      </div>

      <div className="other-store-field">
        <label className="other-store-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(event) => setConsent(event.target.checked)}
          />
          <span>
            Чтобы оформить заказ, нужно дать согласие{" "}
            <a
              href={privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="other-store-consent-link"
              onClick={(event) => event.stopPropagation()}
            >
              на обработку персональных данных
            </a>
            . Я даю согласие.
          </span>
        </label>
      </div>

      <div className="other-store-field other-store-email-field">
        <span className="other-store-field-label">Email для получения кода</span>
        <div
          className="other-store-input-wrap"
          onMouseEnter={() => {
            if (!consent) {
              setEmailHintVisible(true);
            }
          }}
          onMouseLeave={() => setEmailHintVisible(false)}
        >
          <input
            type="email"
            className={`other-store-input${!consent ? " other-store-input--locked" : ""}`}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="example@mail.ru"
            readOnly={!consent}
            onFocus={() => {
              if (!consent) {
                setEmailHintVisible(true);
              }
            }}
            onBlur={() => setEmailHintVisible(false)}
            autoComplete="email"
          />
          {emailHintVisible && !consent ? (
            <span className="other-store-field-hint" role="status">
              Нажмите галочку выше
            </span>
          ) : null}
        </div>
      </div>

      {error ? <p className="other-store-error">{error}</p> : null}

      {priceNotice ? (
        <p className="other-store-price-notice" role="status">
          {priceNotice}
        </p>
      ) : null}

      <div className="other-store-purchase-footer">
        <button
          type="button"
          className={`other-store-buy-btn${
            isCryptoPayment ? " other-store-buy-btn-crypto" : ""
          }`}
          disabled={!canPurchase}
          onClick={handlePurchase}
        >
          {getPurchaseButtonLabel()}
        </button>

        <p className="other-store-payment-hint">
          {isCryptoPayment
            ? "После нажатия «Оплатить» страница оплаты USDT откроется в новой вкладке."
            : "После нажатия «Оплатить» страница оплаты откроется в новой вкладке."}{" "}
          У вас будет {paymentLifetimeMinutes} мин. на оплату.
        </p>

        <label className="other-store-crypto-toggle">
          <input
            type="checkbox"
            checked={isCryptoPayment}
            onChange={(event) => setIsCryptoPayment(event.target.checked)}
          />
          <span>
            Оплатить криптовалютой
            {totalPriceUsdt > 0 ? (
              <>
                {" "}
                <span className="other-store-crypto-hint">
                  ({formatUsdtPrice(totalPriceUsdt)})
                </span>
              </>
            ) : null}
          </span>
        </label>

        {testingPeriodEnabled ? (
          <div className="other-store-testing-banner" role="status">
            <p className="other-store-testing-banner-text">
              Временно идёт тестирование нового функционала, могут встречаться
              ошибки, о которых можно сообщать в техподдержку:{" "}
              <a
                href={support.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="other-store-consent-link"
              >
                Telegram
              </a>
              {", "}
              <a
                href={support.vk}
                target="_blank"
                rel="noopener noreferrer"
                className="other-store-consent-link"
              >
                ВКонтакте
              </a>
              {", "}
              <a
                href={`mailto:${support.email}`}
                className="other-store-consent-link"
              >
                {support.email}
              </a>
            </p>
          </div>
        ) : null}
      </div>
        </div>
      ) : null}
    </aside>
  );
}

export default OtherStorePurchasePanel;
